import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { CheckSquare, Square, Flag } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export const ReferralPipeline = () => {
  const [status, setStatus] = useState("open");
  const [flags, setFlags] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/referral-flags", { params: { status } });
      setFlags(data.flags || []); setOpenCount(data.open_count || 0);
    } catch (_) { toast.error("Couldn't load referrals"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (f) => {
    try {
      await api.post(`/admin/referral-flags/${f.referral_id}/handle`, { handled: !f.handled });
      toast.success(!f.handled ? `Marked ${f.company_name} as handled` : "Re-opened");
      load();
    } catch (_) { toast.error("Couldn't update referral"); }
  };

  return (
    <div data-testid="referral-pipeline">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="flex items-center gap-2 text-sm font-bold text-neutral-200">
          <Flag size={15} className="text-amber-300" /> Outsourced partner referrals
          <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-bold text-amber-300" data-testid="referral-open-count">{openCount} open</span>
        </p>
        <div className="flex gap-1">
          {[["open", "Open"], ["handled", "Handled"], ["all", "All"]].map(([v, l]) => (
            <button key={v} onClick={() => setStatus(v)}
              className={`rounded-full px-3 py-1 text-xs font-bold ${status === v ? "bg-[#0A8B85]/20 text-[#2dd4bf] ring-1 ring-[#0A8B85]/40" : "bg-neutral-900 text-neutral-400 hover:text-neutral-200"}`}
              data-testid={`referral-filter-${v}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="mt-3 overflow-x-auto rounded-2xl border border-neutral-800">
        <table className="w-full min-w-[900px] text-left text-sm" data-testid="referral-table">
          <thead className="bg-neutral-900 text-[10px] uppercase tracking-wider text-neutral-400">
            <tr>
              <th className="px-3 py-3 w-10">Done</th>
              <th className="px-3 py-3">Flagged</th>
              <th className="px-3 py-3">Founder</th>
              <th className="px-3 py-3">Company</th>
              <th className="px-3 py-3">Flagged by</th>
              <th className="px-3 py-3">Founder's need</th>
              <th className="px-3 py-3">Handled</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-800">
            {loading ? (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-neutral-500">Loading…</td></tr>
            ) : flags.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-neutral-500" data-testid="referral-empty">
                {status === "open" ? "No open referrals — nothing waiting on the team." : "No referrals here."}
              </td></tr>
            ) : flags.map((f) => (
              <tr key={f.referral_id} className={f.handled ? "opacity-60" : ""} data-testid={`referral-row-${f.referral_id}`}>
                <td className="px-3 py-2">
                  <button onClick={() => toggle(f)} title={f.handled ? "Re-open" : "Mark as handled"}
                    className={`rounded p-1 ${f.handled ? "text-emerald-400" : "text-neutral-500 hover:text-[#2dd4bf]"}`}
                    data-testid={`referral-toggle-${f.referral_id}`}>
                    {f.handled ? <CheckSquare size={18} /> : <Square size={18} />}
                  </button>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-neutral-300">{fmt(f.created_at)}</td>
                <td className="px-3 py-2">
                  <p className="font-semibold text-neutral-100">{f.client_name}</p>
                  <p className="text-[11px] text-neutral-500">{f.client_email}</p>
                </td>
                <td className="px-3 py-2 text-neutral-200">{f.company_name}{f.sector ? <span className="block text-[11px] text-neutral-500">{f.sector}</span> : null}</td>
                <td className="px-3 py-2 text-neutral-300">{f.rm_name || "—"}</td>
                <td className="px-3 py-2 max-w-md text-neutral-300"><p className="line-clamp-3" title={f.note}>{f.note}</p></td>
                <td className="px-3 py-2 text-[11px] text-neutral-400 whitespace-nowrap">
                  {f.handled ? <>{fmt(f.handled_at)}<span className="block text-neutral-500">{f.handled_by}</span></> : <span className="text-amber-300">Open</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
